import { LEAD_DROP_POPUP_1_CONSTANTS } from "./leadDropPopup_1";

/**
 * Lead Popup 8 — IIT-style phone-first flow (twin of Popup 7), but configured the
 * "variant 1/2 way": no raw `dynamic_field_definitions` JSON textarea. Instead the
 * four canonical post-OTP fields (Name / Email / Qualification / Program) expose
 * explicit label + placeholder + required inputs.
 *
 * `Leaddrop8` is an unchanged copy of `Leaddrop7`, so it still reads its secondary
 * fields from `dynamic_field_definitions`. {@link buildLeadDrop8DynamicDefinitions}
 * compiles the explicit config keys into that JSON; the variant config recompiles it
 * on every save (see `variants/popup.ts` / `variants/form.ts`).
 */

/** Default labels/placeholders for the four canonical secondary fields. */
const SECONDARY_FIELD_DEFAULTS = {
  first_name_label: "First Name",
  first_name_placeholder: "Enter your first name",
  first_name_required: true,
  last_name_label: "Last Name",
  last_name_placeholder: "Enter your last name",
  last_name_required: false,
  email_label: "Email",
  email_placeholder: "Enter your email",
  email_required: true,
  qualification_label: "Highest Qualification",
  qualification_placeholder: "Select your qualification",
  qualification_required: true,
  program_label: "Program",
  program_placeholder: "Select your program",
  program_required: true,
} as const;

/**
 * Compile the explicit label/placeholder/required keys into the
 * `dynamic_field_definitions` JSON array the (unchanged) Leaddrop8 component reads.
 * Field id/kind/bind are fixed; only the copy + required flag are admin-tunable.
 */
export function buildLeadDrop8DynamicDefinitions(
  row: Record<string, unknown> | null | undefined,
): string {
  const s = (key: string, fallback: string) => {
    const v = row?.[key];
    return typeof v === "string" && v.trim() !== "" ? v : fallback;
  };
  const b = (key: string, fallback: boolean) =>
    typeof row?.[key] === "boolean" ? (row[key] as boolean) : fallback;

  return JSON.stringify([
    {
      id: "first_name",
      kind: "text",
      label: s("first_name_label", SECONDARY_FIELD_DEFAULTS.first_name_label),
      placeholder: s(
        "first_name_placeholder",
        SECONDARY_FIELD_DEFAULTS.first_name_placeholder,
      ),
      required: b("first_name_required", SECONDARY_FIELD_DEFAULTS.first_name_required),
      bind: "first_name",
    },
    {
      id: "last_name",
      kind: "text",
      label: s("last_name_label", SECONDARY_FIELD_DEFAULTS.last_name_label),
      placeholder: s(
        "last_name_placeholder",
        SECONDARY_FIELD_DEFAULTS.last_name_placeholder,
      ),
      required: b("last_name_required", SECONDARY_FIELD_DEFAULTS.last_name_required),
      bind: "last_name",
    },
    {
      id: "email",
      kind: "email",
      label: s("email_label", SECONDARY_FIELD_DEFAULTS.email_label),
      placeholder: s("email_placeholder", SECONDARY_FIELD_DEFAULTS.email_placeholder),
      required: b("email_required", SECONDARY_FIELD_DEFAULTS.email_required),
      bind: "email",
    },
    {
      id: "qualification",
      kind: "qualification",
      label: s("qualification_label", SECONDARY_FIELD_DEFAULTS.qualification_label),
      placeholder: s(
        "qualification_placeholder",
        SECONDARY_FIELD_DEFAULTS.qualification_placeholder,
      ),
      required: b("qualification_required", SECONDARY_FIELD_DEFAULTS.qualification_required),
      bind: "qualification",
    },
    {
      id: "course",
      kind: "course",
      label: s("program_label", SECONDARY_FIELD_DEFAULTS.program_label),
      placeholder: s("program_placeholder", SECONDARY_FIELD_DEFAULTS.program_placeholder),
      required: b("program_required", SECONDARY_FIELD_DEFAULTS.program_required),
      bind: "course",
    },
  ]);
}

/**
 * Same last-screen imagery/copy keys hidden in admin as Popup 7: they stay in
 * DEFAULT_ROW (loaded from API) but are not surfaced as editable fields here.
 */
const LAST_SCREEN_FIELDS_HIDDEN_IN_ADMIN = new Set([
  "existing_user_desktop_image",
  "existing_user_mobile_image",
  "existing_user_image_alt",
  "existing_user_title",
  "existing_user_description",
  "existing_user_title_color",
  "existing_user_description_color",
  "new_user_desktop_image",
  "new_user_mobile_image",
  "new_user_image_alt",
  "new_user_title",
  "new_user_description",
  "new_user_title_color",
  "new_user_description_color",
  "brochure_desktop_image",
  "brochure_mobile_image",
  "brochure_image_alt",
  "brochure_title",
  "brochure_description",
  "brochure_title_color",
  "brochure_description_color",
  "brochure_link_color",
]);

const POPUP8_SHARED_FIELDS_FROM_1 =
  LEAD_DROP_POPUP_1_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG.filter(
    (f) =>
      typeof f.fieldName === "string" &&
      !LAST_SCREEN_FIELDS_HIDDEN_IN_ADMIN.has(f.fieldName),
  );

/** Explicit, variant-1-style config for the four canonical post-OTP fields. */
const SECONDARY_FIELDS_CONFIG = [
  {
    fieldName: "first_name_label",
    label: "First name — field label",
    value: SECONDARY_FIELD_DEFAULTS.first_name_label,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.first_name_label,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.first_name_label,
    isJsonField: true,
  },
  {
    fieldName: "first_name_placeholder",
    label: "First name — field placeholder",
    value: SECONDARY_FIELD_DEFAULTS.first_name_placeholder,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.first_name_placeholder,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.first_name_placeholder,
    isJsonField: true,
  },
  {
    fieldName: "first_name_required",
    label: "First name — required",
    value: SECONDARY_FIELD_DEFAULTS.first_name_required,
    valueType: "boolean",
    filedType: "CHECKBOX",
    placeholder: "Make the first name field mandatory",
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.first_name_required,
    isJsonField: true,
  },
  {
    fieldName: "last_name_label",
    label: "Last name — field label",
    value: SECONDARY_FIELD_DEFAULTS.last_name_label,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.last_name_label,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.last_name_label,
    isJsonField: true,
  },
  {
    fieldName: "last_name_placeholder",
    label: "Last name — field placeholder",
    value: SECONDARY_FIELD_DEFAULTS.last_name_placeholder,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.last_name_placeholder,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.last_name_placeholder,
    isJsonField: true,
  },
  {
    fieldName: "last_name_required",
    label: "Last name — required (optional field)",
    value: SECONDARY_FIELD_DEFAULTS.last_name_required,
    valueType: "boolean",
    filedType: "CHECKBOX",
    placeholder: "Make the last name field mandatory",
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.last_name_required,
    isJsonField: true,
  },
  {
    fieldName: "email_label",
    label: "Email — field label",
    value: SECONDARY_FIELD_DEFAULTS.email_label,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.email_label,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.email_label,
    isJsonField: true,
  },
  {
    fieldName: "email_placeholder",
    label: "Email — field placeholder",
    value: SECONDARY_FIELD_DEFAULTS.email_placeholder,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.email_placeholder,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.email_placeholder,
    isJsonField: true,
  },
  {
    fieldName: "email_required",
    label: "Email — required",
    value: SECONDARY_FIELD_DEFAULTS.email_required,
    valueType: "boolean",
    filedType: "CHECKBOX",
    placeholder: "Make the email field mandatory",
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.email_required,
    isJsonField: true,
  },
  {
    fieldName: "qualification_label",
    label: "Qualification — field label",
    value: SECONDARY_FIELD_DEFAULTS.qualification_label,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.qualification_label,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.qualification_label,
    isJsonField: true,
  },
  {
    fieldName: "qualification_placeholder",
    label: "Qualification — dropdown placeholder",
    value: SECONDARY_FIELD_DEFAULTS.qualification_placeholder,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.qualification_placeholder,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.qualification_placeholder,
    isJsonField: true,
  },
  {
    fieldName: "qualification_required",
    label: "Qualification — required",
    value: SECONDARY_FIELD_DEFAULTS.qualification_required,
    valueType: "boolean",
    filedType: "CHECKBOX",
    placeholder: "Make the qualification field mandatory",
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.qualification_required,
    isJsonField: true,
  },
  {
    fieldName: "program_label",
    label: "Program — field label",
    value: SECONDARY_FIELD_DEFAULTS.program_label,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.program_label,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.program_label,
    isJsonField: true,
  },
  {
    fieldName: "program_placeholder",
    label: "Program — dropdown placeholder",
    value: SECONDARY_FIELD_DEFAULTS.program_placeholder,
    valueType: "string",
    filedType: "INPUT",
    placeholder: SECONDARY_FIELD_DEFAULTS.program_placeholder,
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.program_placeholder,
    isJsonField: true,
  },
  {
    fieldName: "program_required",
    label: "Program — required",
    value: SECONDARY_FIELD_DEFAULTS.program_required,
    valueType: "boolean",
    filedType: "CHECKBOX",
    placeholder: "Make the program field mandatory",
    required: false,
    defaultValue: SECONDARY_FIELD_DEFAULTS.program_required,
    isJsonField: true,
  },
];

/** Lead Popup 8 — IIT phone-first flow with explicit (non-JSON) secondary-field config. */
export const LEAD_DROP_POPUP_8_CONSTANTS = {
  DEFAULT_ROW: {
    ...LEAD_DROP_POPUP_1_CONSTANTS.DEFAULT_ROW,
    form_title: "Please Enter Your Phone No.",
    form_title_color: "#DE5803",
    form_sub_text: "",
    button_color: "#DE5803",
    checkbox_color: "#DE5803",
    edit_text_color: "#DE5803",
    resend_text_color: "#DE5803",
    brochure_link_color: "#DE5803",
    signup_button_text: "Continue",
    get_otp_button_text: "Get OTP",
    verify_otp_button_text: "Verify OTP",
    get_otp_enabled_color: "#DE5803",
    get_otp_disabled_color: "#D4D4D8",
    phone_row_background_color: "#F0F0F7",
    redirection_url: "",
    desktop_image_bg_color: "#CD9986",
    success_button_text_with_pok: "Continue with Your Application",
    success_button_text_without_pok: "Close",
    success_button_link: "",
    ...SECONDARY_FIELD_DEFAULTS,
    // Canonical secondary-field JSON the unchanged Leaddrop8 component reads.
    // Recompiled from the explicit keys above on every admin save.
    dynamic_field_definitions: buildLeadDrop8DynamicDefinitions(null),
  },

  LEAD_DROP_POPUP_FIELDS_CONFIG: [
    {
      fieldName: "get_otp_button_text",
      label: "Phone-step button label",
      value: "Get OTP",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Get OTP",
      required: false,
      defaultValue: "Get OTP",
      isJsonField: true,
    },
    {
      fieldName: "verify_otp_button_text",
      label: "OTP-step button label",
      value: "Verify OTP",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Verify OTP",
      required: false,
      defaultValue: "Verify OTP",
      isJsonField: true,
    },
    {
      fieldName: "get_otp_enabled_color",
      label: '"Get OTP" enabled color',
      value: "#DE5803",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#DE5803",
      required: false,
      defaultValue: "#DE5803",
      isJsonField: true,
    },
    {
      fieldName: "get_otp_disabled_color",
      label: '"Get OTP" disabled color',
      value: "#D4D4D8",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#D4D4D8",
      required: false,
      defaultValue: "#D4D4D8",
      isJsonField: true,
    },
    {
      fieldName: "phone_row_background_color",
      label: "Phone row background",
      value: "#F0F0F7",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F0F0F7",
      required: false,
      defaultValue: "#F0F0F7",
      isJsonField: true,
    },
    {
      fieldName: "redirection_url",
      label: "Redirection URL after submit (e.g. https://abc)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://abc",
      required: false,
      defaultValue: "",
      isJsonField: true,
    },
    {
      fieldName: "desktop_image_bg_color",
      label: "Desktop image background color",
      value: "#CD9986",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#CD9986",
      required: false,
      defaultValue: "#CD9986",
      isJsonField: true,
    },
    {
      fieldName: "success_button_text_with_pok",
      label: "Success button text — when the course HAS a POK (redirects to application)",
      value: "Continue with Your Application",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Continue with Your Application",
      required: false,
      defaultValue: "Continue with Your Application",
      isJsonField: true,
    },
    {
      fieldName: "success_button_text_without_pok",
      label: "Success button text — when the course has NO POK (just closes the popup)",
      value: "Close",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Close",
      required: false,
      defaultValue: "Close",
      isJsonField: true,
    },
    {
      fieldName: "success_button_link",
      label:
        "Success button link (optional) — opens in a new tab when set; leave empty to keep the current behaviour (redirect to application or close)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://example.com/... (leave empty to keep current behaviour)",
      required: false,
      defaultValue: "",
      isJsonField: true,
    },
    ...SECONDARY_FIELDS_CONFIG,
    // terms_conditions_link + privacy_policy_link are inherited from popup 1 via
    // POPUP8_SHARED_FIELDS_FROM_1 above (do not re-add them here, or the admin
    // editor shows the fields twice).
    ...POPUP8_SHARED_FIELDS_FROM_1,
  ],

  METADATA_FIELDS: LEAD_DROP_POPUP_1_CONSTANTS.METADATA_FIELDS,
};
